/***************************************************************
 * Copyright (c) 2006 - 2022, Case Anywhere, LLC.
 * All rights reserved
 *
 * @author Brad Wetmore
 * Date: 04/01/2022
 *
 * @version 0.2
 * Revisions:
 * 04/01/2022 - BW - Initial Creation
 * 04/22/2022 - BW - Modified: addDynamicValidation - added code for Payment = $0
 *
 * Description/Purpose:
 * Payment Validation
 ***************************************************************/
import StackTraceError from "component/stack-trace-error/stack-trace-error";
import ValidateForm from "util/validate-form";
import {COMMON, TAG_DATA, VALIDATION_MESSAGE} from "page/payment/payment-util";
import {
  COMMON as COMMON_PAYMENT_EXTRA,
  VALIDATION_MESSAGE as VALIDATION_MESSAGE_PAYMENT_EXTRA
} from "page/payment/payment-extra-util";
import PaymentExtraValidation from "page/payment/payment-extra-validation";
import {PAGE_PARAM, VALIDATION_FORM} from "constant/app-constant";
import {InputUtil} from "util/input-util";
import TableUtil from "component/table/table-util";
import {ValidateFormUtil} from "../../util/validate-form-util";


export default class PaymentValidation {

  /**
   *
   * @param $form
   * @param options
   * @returns {PaymentValidation}
   */
  constructor($form, options = []) {
    const defaults = {
    };
    // extends config
    this.opts = $.extend(true, defaults, options);

    this.$form = $form;

    this.validateForm = new ValidateForm(this.$form, this.opts);
    this.paymentExtraValidation = new PaymentExtraValidation(this.validateForm, this.opts);

    this.setUpVariable();
    this.cacheDom();
    this.registerObject();
    this.registerEvent();

    return this;
  }


  /**
   * Set up some common variables
   */
  setUpVariable() {
    return this;
  }


  /**
   * Caches some often used DOM objects
   * @returns {PaymentValidation}
   */
  cacheDom() {
    this.$table = $(TableUtil.TABLE_SELECTOR);
    this.guestDisplay = TAG_DATA.GUEST_DISPLAY;
    this.$invoiceNotListed = $(COMMON.INVOICE_NOT_LISTED_SELECTOR);
    this.$totalRowCount = $(`#${PAGE_PARAM.TOTAL_ROW_COUNT}`) ;
    return this;
  }


  /**
   * Set up some objects
   */
  registerObject() {
    return this;
  }


  /**
   * Register events or DOM event
   */
  registerEvent() {
    try {
      this.registerValidateForm();
    } catch (e) {
      StackTraceError.processError(e, 'Payment Validation - Register Event');
    }
  }


  /**
   * Register the ValidateForm Event and setup the object
   */
  registerValidateForm() {
    const validateForm = new ValidateForm(this.$form)
    const totalRowCount = parseInt(this.$totalRowCount.val());
    this.registerValidateFormBase(validateForm);
    if (this.guestDisplay) {
      this.registerValidateFormGuest(validateForm);
      this.registerValidateFormInvoicePaymentExtra(validateForm);
    } else if (!this.guestDisplay) {
      this.addDynamicValidation(validateForm);
    } else if (!this.guestDisplay && totalRowCount === 0) {
      this.registerValidateFormInvoicePaymentExtra(validateForm);
    }
    if (this.guestDisplay || InputUtil.isCheckBoxChecked(this.$invoiceNotListed))
      this.paymentExtraValidation.addDynamicValidation(validateForm);
    validateForm.init();
  }


  /**
   * Register the ValidateForm Event and setup the object
   * @param validateForm
   * @returns {string}
   */
  registerValidateFormBase(validateForm) {
    try {
      const _this = this;
      validateForm
          .addRules({
            invoice_payment_psv: {
              required: true,
              normalizer(value) {
                return _this.validationFormPSVNormalizer(value);
              },
            }
          })
          .addMessages({
            invoice_payment_psv: { required: VALIDATION_MESSAGE.SIZE },
          })
      // .init();
      // console.log( this.validateForm );
    } catch (e) {
      StackTraceError.processError(e, 'Payment Validation - Register Validate Form');
    }
  }


  /**
   * Validation Form - Normalizer function
   * @param value
   * @returns {string}
   */
  validationFormPSVNormalizer(value) {
    this._validationFormNormalizer = true; // eslint
    return $(`${COMMON.INVOICE_PAYMENT_PSV_SELECTOR}`).val().length === 0 &&
    $(`${COMMON_PAYMENT_EXTRA.INVOICE_PAYMENT_EXTRA_PSV_SELECTOR}`).val().length === 0 ? '' : 'valid';
    // return $(`.${COMMON.PARTY_MODAL_LI_CLASS} input:checkbox:checked`).length === 0 ? '' : 'valid';
  }


  /**
   * Register the ValidateForm Event and setup the object
   * @param validateForm
   */
  registerValidateFormGuest(validateForm) {
    try {
      const _this = this;
      validateForm
          .addRules({
            organization_name: "required",
            email_address: {
              required: true,
              email: true,
            },
          })
          .addMessages({
            organization_name: {
              required: VALIDATION_MESSAGE.ORGANIZATION_NAME_REQUIRED,
            },
            email_address: {
              required: VALIDATION_MESSAGE.EMAIL_ADDRESS_REQUIRED,
              email: VALIDATION_MESSAGE.EMAIL_ADDRESS_VALID_EMAIL,
            },
          })
      // .init();
      // console.log( this.validateForm );
    } catch (e) {
      StackTraceError.processError(e, 'Payment Validation - Register Validate Form');
    }
  }


  /**
   * Register the ValidateForm Event and setup the object
   * @param validateForm
   * @returns {string}
   */
  registerValidateFormInvoicePaymentExtra(validateForm) {
    try {
      const _this = this;
      validateForm
          .addRules({
            invoice_payment_extra_psv: {
              required: true,
              normalizer(value) {
                return _this.validationFormNormalizer(value);
              }
            }
          })
          .addMessages({
            invoice_payment_extra_psv: {
              required: VALIDATION_MESSAGE_PAYMENT_EXTRA.SIZE,
            },
          })
      // .init();
      // console.log( this.validateForm );
    } catch (e) {
      StackTraceError.processError(e, 'Payment Validation - Register Validate Form');
    }
  }


  /**
   * Validation Form - Normalizer function
   * @param value
   * @returns {string}
   */
  validationFormNormalizer(value) {
    this._validationFormNormalizer = true; // eslint
    const isValid = $(`.${COMMON_PAYMENT_EXTRA.PAYMENT_EXTRA_PAYMENT_INPUT}`).val() === '' ? '' : 'valid';
    const firstInvoiceNumber = $(`.${COMMON_PAYMENT_EXTRA.PAYMENT_EXTRA_INVOICE_NUMBER_INPUT}:first`);
    const firstPayment = $(`.${COMMON_PAYMENT_EXTRA.PAYMENT_EXTRA_PAYMENT_INPUT}:first`);
    if (isValid) {
      firstInvoiceNumber.removeClass(VALIDATION_FORM.INVALID_CLASS);
      firstPayment.removeClass(VALIDATION_FORM.INVALID_CLASS);
    } else {
      firstInvoiceNumber.addClass(VALIDATION_FORM.INVALID_CLASS);
      firstPayment.addClass(VALIDATION_FORM.INVALID_CLASS);
    }
    return isValid;
  }


  /**
   * Adds the dynamic validation rules for when users add/remove bates range entries
   */
  addDynamicValidation(validateForm) {
    let paymentSSV = '';
    const paymentRuleObj = {};
    const paymentMessageObj = {};

    // $(`${TableUtil.TABLE_SELECTOR} tr`).addClass(TableUtil.SELECTED_CLASS)
    // this.$table.find(`tr.${TableUtil.SELECTED_CLASS}`)
    this.$table.find(`tbody tr`)
        .each((index, value) => {
          const $tr = $(value);

          // Grab each "required" input ( being and end)
          const $payment = $tr.find(`.${COMMON.TABLE_PAYMENT_CLASS}`);
          const paymentName = $payment.attr('name');

          // Process the group data first
          paymentSSV += paymentSSV.length > 0 ? ' ' : '';
          paymentSSV += `${paymentName}`;

          // Next add the rules for each element
          paymentRuleObj[paymentName] =
              {
                required: true,
                currency: true,
                paymentMoreThanBalance: true,
                paymentZero: true,
              };

          // Finally add the messages for each element
          paymentMessageObj[paymentName] = {
            required: VALIDATION_MESSAGE.TABLE_AMOUNT_REQUIRED,
            currency: VALIDATION_MESSAGE.TABLE_AMOUNT_CURRENCY,
            paymentMoreThanBalance: VALIDATION_MESSAGE.TABLE_AMOUNT_MORE_THAN_BALANCE,
            paymentZero: VALIDATION_MESSAGE.TABLE_AMOUNT_ZERO,
          };
        });

    // Add to the ValidationForm if necessary
    if (paymentSSV.length > 0) {
      validateForm
          .addGroups({paymentTableGroup: paymentSSV})
          .addRules(paymentRuleObj)
          .addMessages(paymentMessageObj)
      // .init();
      console.log( validateForm );
      ValidateFormUtil.addCurrency();
      ValidateFormUtil.addPaymentMoreThanBalance();
      ValidateFormUtil.addPaymentZero();
      // ValidateFormUtil.addValidateInvoiceNumber();
    }
  }


  /**
   * Resets form
   */
  resetForm() {
    this.validateForm.resetForm()
  }


  /**
   * Resets
   */
  reset() {
    this.validateForm.reset()
  }


}
